import React from 'react';
import { Link } from 'react-router-dom';
import { urlFor, getImageAlt } from '../../lib/sanity';
import { getPetFullUrl } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';
import { Heart } from 'lucide-react';

interface SimilarPetsProps {
  pets: Pet[];
  currentPetId: string;
}

export function SimilarPets({ pets, currentPetId }: SimilarPetsProps) {
  // Filter out current pet and limit to 3
  const similarPets = pets.filter(pet => pet._id !== currentPetId).slice(0, 3);

  if (similarPets.length === 0) {
    return null;
  }

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + 
      (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} mies.`;
    }
    
    const age = Math.floor(ageInMonths / 12);
    return `${age} ${age === 1 ? 'rok' : age < 5 ? 'lata' : 'lat'}`;
  };

  return (
    <div className="mt-12 pt-12 border-t border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-white/20">
      <h2 className="heading-3 mb-6">Inne zwierzęta do adopcji</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {similarPets.map((pet) => (
          <Link
            key={pet._id}
            to={getPetFullUrl(pet)}
            className="bg-white dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden flex flex-col hover:shadow-xl transition-shadow duration-300"
          >
            <div className="relative aspect-video overflow-hidden">
              <img
                src={urlFor(pet.image) || 'https://placehold.co/800x600/jpeg'}
                alt={getImageAlt(pet.image, pet)}
                className="w-full h-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-accessible-red-600/90 text-white text-xs font-bold rounded-full">
                  {pet.mainCategory === 'dog' ? 'Pies' : 'Kot'}
                </span>
              </div>
            </div>

            <div className="p-6 flex-1 flex flex-col">
              <h3 className="heading-5 mb-2 text-accessible-gray-900 dark:text-white high-contrast:text-white">
                {pet.name}
              </h3>
              
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-4 h-4 text-accessible-red-600" />
                <span className="text-sm text-accessible-gray-600 dark:text-white/80 high-contrast:text-white/70">
                  {calculateAge(pet.birthDate)}
                </span>
              </div>

              {pet.description && (
                <p className="body-small text-accessible-gray-600 dark:text-white/80 high-contrast:text-white/70 line-clamp-3 mb-4">
                  {pet.description.substring(0, 100)}...
                </p>
              )}

              <div className="mt-auto">
                <span className="text-accessible-red-600 hover:text-accessible-red-700 text-sm font-medium">
                  Zobacz profil →
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

